import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { libraryQuestionPath } from '@/constants/appRoutes';
import {
  libraryQuestionsApi,
  type LibraryQuestion,
  type LibraryQuestionChannel,
} from '@/services/api';
import { useChatShell } from '../ChatShellContext';
import { ChatInput, type ChatInputProps } from './ChatInput';

type ComposerInputProps = Omit<ChatInputProps, 'onSubmit' | 'askMode' | 'loading' | 'showModes'> & {
  onAiSubmit: (e: React.FormEvent) => void;
  loading: boolean;
};

export function ChatComposer({
  onAiSubmit,
  loading,
  input,
  onInputChange,
  ...rest
}: ComposerInputProps) {
  const { askMode, noteCommunityPost } = useChatShell();
  const [notice, setNotice] = useState<LibraryQuestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    if (askMode === 'ai') {
      setNotice(null);
      setError(null);
      onAiSubmit(e);
      return;
    }
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    void libraryQuestionsApi
      .create({ channel: askMode as LibraryQuestionChannel, body: text })
      .then((created) => {
        setNotice(created);
        onInputChange('');
        if (created.channel === 'community') noteCommunityPost();
      })
      .catch(() => {
        setError('Could not send your question. Try again.');
      })
      .finally(() => setSending(false));
  };

  return (
    <>
      {notice && (
        <div className="flex-shrink-0 px-4">
          <div className="mx-auto w-full max-w-[720px] rounded-xl border border-border bg-card px-4 py-3 text-sm">
            <p className="text-foreground">
              Your question was sent to {notice.channel === 'staff' ? 'the RCM team' : 'the community'}.
            </p>
            <Link
              to={libraryQuestionPath(notice.id)}
              className="mt-1 inline-block font-medium text-primary hover:underline"
            >
              View it in the Library
            </Link>
          </div>
        </div>
      )}
      {error && (
        <p className="flex-shrink-0 px-4 text-center text-sm text-destructive">{error}</p>
      )}
      <ChatInput
        {...rest}
        input={input}
        onInputChange={onInputChange}
        onSubmit={handleSubmit}
        loading={loading || sending}
        askMode={askMode}
        showModes
      />
    </>
  );
}
